import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Text } from "src/apps/components/typography/Texts";
import { T } from "src/apps/components/i18n/Translation";
import { ICON_PATHS } from "src/apps/components/icon/IconSVGPaths";
import { Icon, ICON_SIZES } from "src/apps/components/icon/Icon";

import { AvailableActor } from "src/contexts/documentationArea/domain/AvailableActor";
import { ActorId } from "src/contexts/documentationArea/domain/ActorId";
import { useActorSelector } from "src/apps/hooks/useActorSelector";
import { useClickOutside } from "src/apps/hooks/useClickOutside";
import { ActorSelectorContent } from "./ActorSelectorContent";
import { ACTOR_SELECTOR_READ_ONLY, ReadOnlyPill } from "./ActorItem";
import { MEDIA_MOBILE_BREAKPOINT } from "src/apps/config/constants";
import { useTranslations } from "src/apps/hooks/useTranslations";
import { useBodyScrollLock } from "src/apps/hooks/useBodyScrollLock";
import { colors } from "@permisso/permisso-components";

const ACTOR_SELECTOR_YOU = "documentation_area.actor_selector.you";
const ACTOR_SELECTOR_ARIA_LABEL = "documentation_area.actor_selector.aria_label";

type ActorSelectorProps = {
  availableActors: AvailableActor[];
  selectedActorId: ActorId | null;
  representativeIdNumber?: string | null;
  onSelectActor: (actorId: ActorId) => void;
  blackColor: string;
  isDesktop: boolean;
  isMobile: boolean;
};

export const ActorSelector = ({
  availableActors,
  selectedActorId,
  representativeIdNumber,
  onSelectActor,
  blackColor,
  isDesktop,
  isMobile,
}: ActorSelectorProps) => {
  const { selectedActor, isOwner, hasMultipleActors } = useActorSelector({
    availableActors,
    selectedActorId,
  });

  const { t } = useTranslations();
  const [isSelectorOpen, setIsSelectorOpen] = useState(false);
  useBodyScrollLock(isMobile && isSelectorOpen);
  const selectorContainerRef = useRef<HTMLDivElement>(null);

  useClickOutside({
    ref: selectorContainerRef,
    isActive: isSelectorOpen && isDesktop,
    onClickOutside: () => setIsSelectorOpen(false),
  });

  const handleSelectActor = (actorId: ActorId) => {
    if (selectedActorId !== actorId) {
      onSelectActor(actorId);
    }
    setIsSelectorOpen(false);
  };

  // Handle Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isSelectorOpen) {
        setIsSelectorOpen(false);
      }
    };

    if (isSelectorOpen) {
      document.addEventListener("keydown", handleEscape);
      return () => {
        document.removeEventListener("keydown", handleEscape);
      };
    }
  }, [isSelectorOpen]);

  return (
    <ActorInfoContainer ref={selectorContainerRef}>
      <SelectorContentRow>
        <SelectorArea>
          <Icon
            path={hasMultipleActors ? ICON_PATHS.USERS : ICON_PATHS.USER}
            size={ICON_SIZES.s24}
            color={blackColor}
          />
          <SelectorTrigger
            type="button"
            aria-expanded={isSelectorOpen}
            aria-haspopup="listbox"
            aria-label={t(ACTOR_SELECTOR_ARIA_LABEL)}
            onClick={() => setIsSelectorOpen((value) => !value)}
          >
            <ActorTextWrapper>
              <SelectedActorText className="text mp-mask">
                {selectedActor?.fullName}
              </SelectedActorText>
              {selectedActor && isOwner(selectedActor.id) ? (
                <YouLabel>
                  <T k={t(ACTOR_SELECTOR_YOU)} />
                </YouLabel>
              ) : selectedActor?.readOnly ? (
                <ReadOnlyPill>
                  <T k={t(ACTOR_SELECTOR_READ_ONLY)} />
                </ReadOnlyPill>
              ) : null}
            </ActorTextWrapper>
            <ChevronContainer $isOpen={isSelectorOpen}>
              <Icon path={ICON_PATHS.CHEVRON} size={ICON_SIZES.s18} color={blackColor} />
            </ChevronContainer>
          </SelectorTrigger>
          {hasMultipleActors && isDesktop && isSelectorOpen ? (
            <SelectorDropdown role="listbox">
              <ActorSelectorContent
                availableActors={availableActors}
                selectedActor={selectedActor}
                onSelectActor={handleSelectActor}
              />
            </SelectorDropdown>
          ) : null}
        </SelectorArea>

        {representativeIdNumber && selectedActor && isOwner(selectedActor.id) ? (
          <IdNumberContainer>
            <Icon path={ICON_PATHS.ID_CARD_ICON} size={ICON_SIZES.s24} color={blackColor} />
            <IdNumberText className="text mp-mask">{representativeIdNumber}</IdNumberText>
          </IdNumberContainer>
        ) : null}
      </SelectorContentRow>

      {hasMultipleActors && isMobile && isSelectorOpen ? (
        <SheetOverlay onClick={() => setIsSelectorOpen(false)}>
          <SheetContainer onClick={(event) => event.stopPropagation()}>
            <SheetHandle />
            <ActorSelectorContent
              availableActors={availableActors}
              selectedActor={selectedActor}
              onSelectActor={handleSelectActor}
            />
          </SheetContainer>
        </SheetOverlay>
      ) : null}
    </ActorInfoContainer>
  );
};

const ActorInfoContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: fit-content;
  @media (max-width: ${MEDIA_MOBILE_BREAKPOINT}rem) {
    width: 100%;
  }
`;

const SelectorContentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  @media (max-width: ${MEDIA_MOBILE_BREAKPOINT}rem) {
    width: 100%;
  }
`;

const SelectorArea = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  @media (max-width: ${MEDIA_MOBILE_BREAKPOINT}rem) {
    flex: 1;
    min-width: 0;
  }
`;

const SelectorTrigger = styled.button`
  display: flex;
  border: 1px solid ${colors.grey500};
  background-color: ${colors.grey0};
  border-radius: 0.5rem;
  height: 2.313rem;
  width: 16.25rem;
  box-sizing: border-box;
  align-items: center;
  justify-content: space-between;
  gap: 0.375rem;
  padding: 0.5rem;
  cursor: pointer;
  @media (max-width: ${MEDIA_MOBILE_BREAKPOINT}rem) {
    width: auto;
    flex: 1;
  }
`;

const SelectedActorText = styled(Text)`
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.5;
  white-space: nowrap;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  margin: 0;
`;

const ActorTextWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  min-width: 0;
  flex: 1;
`;

const YouLabel = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
`;

const ChevronContainer = styled.span<{ $isOpen: boolean }>`
  display: inline-flex;
  transform: ${({ $isOpen }) => ($isOpen ? "rotate(180deg)" : "rotate(0deg)")};
  transition: transform 0.2s ease;
`;

const SelectorDropdown = styled.div`
  position: absolute;
  top: calc(100% + 0.5rem);
  left: 1.875rem;
  width: 16.25rem;
  max-height: 20rem;
  background-color: ${colors.grey0};
  border: 1px solid ${colors.grey500};
  border-radius: 0.5rem;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  z-index: 4;
  animation: dropdownFadeIn 0.2s ease-out;

  @keyframes dropdownFadeIn {
    from {
      opacity: 0;
      transform: translateY(-6px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const IdNumberContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const IdNumberText = styled(Text)`
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.5;
  white-space: nowrap;
`;

const SheetOverlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.32);
  z-index: 20;
  display: flex;
  align-items: flex-end;
  animation: overlayFadeIn 0.2s ease-out;

  @keyframes overlayFadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const SheetContainer = styled.div`
  width: 100%;
  max-height: calc(100vh - 20rem);
  background-color: ${colors.grey0};
  border-top-left-radius: 1rem;
  border-top-right-radius: 1rem;
  padding: 0.75rem 1rem 1.5rem 1rem;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  animation: sheetSlideUp 0.24s ease-out;

  @keyframes sheetSlideUp {
    from {
      opacity: 0;
      transform: translateY(18px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const SheetHandle = styled.div`
  width: 3rem;
  height: 0.375rem;
  border-radius: 0.25rem;
  background-color: ${colors.grey700};
  margin: 0 auto 0.75rem auto;
`;
